// Runtime configuration accessor (A1 — runtime-config injection).
//
// chat-ui ships ONE image to dev AND prod. Per-environment, public config
// (the rag-api URL + the 3 Entra values) is read at container RUNTIME from
// `window.__APP_CONFIG__`, written by the nginx entrypoint
// (docker-entrypoint.d/50-config.sh) from ACA env vars into /config.js —
// loaded by index.html BEFORE the app module. This replaces the previous
// Vite `--build-arg VITE_*` build-time bake (which welded one image to one
// environment).
//
// Resolution order per key:
//   1. window.__APP_CONFIG__[key]   — runtime injection (the image's truth)
//   2. import.meta.env.VITE_<key>   — build/dev fallback (so `vite dev` +
//                                     unit tests keep working unchanged)
//   3. undefined
//
// IMPORTANT: empty strings are normalized to `undefined` so a missing ACA
// env var (which the entrypoint may write as "") does NOT defeat the
// downstream `?? "/"` API-base fallback in client.ts (the `??` operator only
// catches null/undefined, never "").
//
// Only PUBLIC config lives here — the MSAL clientId / authority / audience
// and the API URL are all public (RFC 6749 §2.2). No secrets in the window.

export type AppConfigKey = "RAG_API_URL" | "ENTRA_TENANT_ID" | "ENTRA_CLIENT_ID" | "API_AUDIENCE";

declare global {
  interface Window {
    __APP_CONFIG__?: Partial<Record<AppConfigKey, string>>;
  }
}

// Build-time fallback. Static `import.meta.env.VITE_*` accesses are the ONLY
// form Vite reliably inlines, so they are enumerated here rather than indexed
// dynamically. `vite dev` resolves these from the developer's env; a
// production `vite build` runs with none set (they become undefined), and the
// runtime window config supplies the real values.
const VITE_FALLBACK: Record<AppConfigKey, string | undefined> = {
  RAG_API_URL: import.meta.env.VITE_RAG_API_URL,
  ENTRA_TENANT_ID: import.meta.env.VITE_ENTRA_TENANT_ID,
  ENTRA_CLIENT_ID: import.meta.env.VITE_ENTRA_CLIENT_ID,
  API_AUDIENCE: import.meta.env.VITE_API_AUDIENCE,
};

// Coerce null / undefined / "" to undefined so callers can rely on `?? default`.
function nonEmpty(value: string | undefined): string | undefined {
  return value == null || value === "" ? undefined : value;
}

/**
 * Read a single public runtime-config value.
 * Returns `undefined` (never "") when unset, so a downstream `?? default`
 * resolves correctly.
 */
export function appConfig(key: AppConfigKey): string | undefined {
  return nonEmpty(window.__APP_CONFIG__?.[key]) ?? nonEmpty(VITE_FALLBACK[key]);
}
